import React, { useState, useEffect } from "react";
import Dashboard from "./Dashboard";
import FundSettings from "./FundSettings";
import HistoricalData from "./HistoricalData";
import Transactions from "./Transactions";
import Report from "./Report";
import Financials from "./Financials";
import "./App.css";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react"; // For displaying the QR code

axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// UI PREVIEW MODE - Set to false for production
const UI_PREVIEW_MODE = false;

const App = () => {
  const [page, setPage] = useState(UI_PREVIEW_MODE ? "financials" : "dashboard");
  const [token, setToken] = useState(UI_PREVIEW_MODE ? "preview" : (sessionStorage.getItem("authToken") || null));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [setupMessage, setSetupMessage] = useState("");
  const API_BASE_URL = "https://fundtracker.reicheltcapital.com/api";


useEffect(() => {
  const checkAuthStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const schwabToken = urlParams.get("token");
    
    if (schwabToken) {
      sessionStorage.setItem("authToken", schwabToken);
      sessionStorage.setItem("schwabAuthenticated", "true");
      setToken(schwabToken);
      setPage("dashboard");
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      const storedToken = sessionStorage.getItem("authToken");
      if (storedToken) {
        setToken(storedToken);
        try {
          // Check 2FA status
          const response = await axios.get(`${API_BASE_URL}/check-2fa-status`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          const { requires_2fa, verified } = response.data;
          if (requires_2fa && !verified) {
            setRequires2FA(true);
          } else {
            // Check Schwab tokens if 2FA is not required or is verified
            const tokenCheckResponse = await axios.get(`${API_BASE_URL}/check-schwab-tokens`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            const { valid } = tokenCheckResponse.data;
            if (!valid) {
              window.location.href = `${API_BASE_URL}/auth/schwab`;
            } else {
              setPage("dashboard");
            }
          }
        } catch (error) {
          setMessage("Authentication check failed: " + (error.response?.data?.detail || "Unknown error"));
          handleLogout(); // Log out if token is invalid or other error occurs
        }
      }
    }
  };

  checkAuthStatus();
}, []);

  const handleLogin = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/token`,
        new URLSearchParams({ username, password }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      const newToken = response.data.access_token;
      sessionStorage.setItem("authToken", newToken);
      setToken(newToken);
      setMessage("Login successful!");

      if (response.data.requires_2fa) {
        setRequires2FA(true);
      } else {
        try {
          const tokenCheckResponse = await axios.get(`${API_BASE_URL}/check-schwab-tokens`, {
            headers: { "Authorization": `Bearer ${newToken}` }
          });
          const { valid } = tokenCheckResponse.data;
          if (!valid) {
            window.location.href = `${API_BASE_URL}/auth/schwab`;
          } else {
            setPage("dashboard");
          }
        } catch (checkError) {
          setMessage("Login successful, but Schwab token check failed. Redirecting to authenticate Schwab...");
          window.location.href = `${API_BASE_URL}/auth/schwab`;
        }
      }
    } catch (error) {
      setMessage("Login failed: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handle2FAVerification = async () => {
    try {
      await axios.post(`${API_BASE_URL}/verify-2fa`, {
        username,
        totp_code: totpCode,
      });
      setMessage("2FA verification successful!");
      setRequires2FA(false);
      try {
        const tokenCheckResponse = await axios.get(`${API_BASE_URL}/check-schwab-tokens`);
        const { valid } = tokenCheckResponse.data;
        if (!valid) {
          window.location.href = `${API_BASE_URL}/auth/schwab`;
        } else {
          setPage("dashboard");
        }
      } catch (checkError) {
        setMessage("2FA successful, but Schwab token check failed. Redirecting to authenticate Schwab...");
        window.location.href = `${API_BASE_URL}/auth/schwab`;
      }
    } catch (error) {
      setMessage("2FA failed: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleSetup2FA = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/setup-2fa/${username}`);
      setQrCodeUrl(response.data.qr_code);
      setSetupMessage("Scan this QR code with your authenticator app (e.g., Google Authenticator).");
    } catch (error) {
      setSetupMessage("Failed to set up 2FA: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("schwabAuthenticated");
    setToken(null);
    setMessage("Logged out successfully.");
    setRequires2FA(false);
    setShow2FASetup(false);
    setQrCodeUrl("");
  };

  // If not logged in, show login form
  if (!token) {
    return (
      <div className="login-container">
        <h2>Login</h2>
        <input
          type="text"
          placeholder="Username"
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          onChange={(e) => setPassword(e.target.value)}
        />
        <button onClick={handleLogin}>Login</button>
        <p>{message}</p>
      </div>
    );
  }

  // If 2FA is required, show 2FA verification form
  if (requires2FA) {
    return (
      <div className="login-container">
        <h2>Two-Factor Authentication</h2>
        <input
          type="text"
          placeholder="Enter 6-digit TOTP code"
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value)}
        />
        <button onClick={handle2FAVerification}>Verify</button>
        <p>{message}</p>
      </div>
    );
  }

  // If user wants to set up 2FA, show QR code
if (show2FASetup) {
  return (
    <div className="app">
      <nav>
        <button onClick={() => setPage("dashboard")}>Dashboard</button>
        <button onClick={() => setPage("fund-settings")}>Fund Settings</button>
        <button onClick={() => setPage("historical-data")}>Historical Data</button>
        <button onClick={() => setPage("transactions")}>Transactions</button>
        <button onClick={() => setPage("report")}>Report</button>
        <button onClick={handleLogout}>Logout</button>
      </nav>
      <div className="setup-2fa-container">
        <h2>Setup Two-Factor Authentication</h2>
        {qrCodeUrl ? (
          <>
            <img src={qrCodeUrl} alt="2FA QR Code" style={{ width: '300px', height: '300px', border: '2px solid black' }} />
            <p>{setupMessage}</p>
          </>
        ) : (
          <button onClick={handleSetup2FA}>Generate 2FA QR Code</button>
        )}
      </div>
    </div>
  );
}

  // Main app interface
  return (
    <div className="app">
      <nav>
        <button onClick={() => setPage("dashboard")}>Dashboard</button>
        <button onClick={() => setPage("fund-settings")}>Fund Settings</button>
        <button onClick={() => setPage("historical-data")}>Historical Data</button>
        <button onClick={() => setPage("transactions")}>Transactions</button>
        <button onClick={() => setPage("report")}>Report</button>
        <button onClick={() => setPage("financials")}>Financials</button>
        <button onClick={() => setShow2FASetup(true)}>Setup 2FA</button>
        <button onClick={handleLogout}>Logout</button>
      </nav>
      {page === "dashboard" && <Dashboard />}
      {page === "fund-settings" && <FundSettings />}
      {page === "historical-data" && <HistoricalData />}
      {page === "transactions" && <Transactions />}
      {page === "report" && <Report />}
      {page === "financials" && <Financials />}
    </div>
  );
};

export default App;
